import { GMN, Measuringpoint, MonitoringTube, UploadTask } from "@nens/brostar/openapi-client";
import {
  CreateGMNTask,
  EditGMNTask,
  deliveryContextGMNOptionsType,
  groundwaterAspectOptionsType,
  monitoringPurposeOptionsType,
  qualityRegimeOptionsType,
} from "@nens/brostar/uploadapi-client";

import { isValidEnum } from "./is-valid-enum";

// Utility to deduplicate measuring points by gmwBroId and tubeNumber
export function deduplicateMeasuringPoints(measuringPoints: Measuringpoint[]): Measuringpoint[] {
  const seen = new Set<string>();
  return measuringPoints.filter((mp) => {
    const key = `${mp.gmwBroId}-${mp.tubeNumber}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// Utility to deduplicate monitoring tubes by gmwBroId and tubeNumber
export function deduplicateMonitoringTubes(monitoringTubes: MonitoringTube[]): MonitoringTube[] {
  const seen = new Set<string>();
  return monitoringTubes.filter((mt) => {
    const key = `${mt.gmwBroId}-${mt.tubeNumber}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// EDIT
export const convertGMNToEditTask = (
  object: GMN,
  measuringPoints: Measuringpoint[],
  monitoringTubes: MonitoringTube[]
): EditGMNTask => {
  const {
    deliveryContext,
    name,
    monitoringPurpose,
    groundwaterAspect,
    startDateMonitoring,
    deliveryAccountableParty,
    qualityRegime,
    broId,
  } = object;

  // Deduplicate before mapping
  const uniqueMeasuringPoints = deduplicateMeasuringPoints(measuringPoints);
  const uniqueMonitoringTubes = deduplicateMonitoringTubes(monitoringTubes);

  return {
    sourcedocumentData: {
      measuringPoints:
        uniqueMeasuringPoints.map((mp) => ({
          ...uniqueMonitoringTubes.find(
            (mt) => mt.tubeNumber === mp.tubeNumber && mt.gmwBroId === mp.gmwBroId
          ),
          broId: mp.gmwBroId + "",
          tubeNumber: mp.tubeNumber + "",
          measuringPointCode: mp.measuringpointCode + "",
        })) ?? [],
      objectIdAccountableParty: "",
      name: name ?? "",
      deliveryContext: isValidEnum(deliveryContextGMNOptionsType, deliveryContext),
      monitoringPurpose: isValidEnum(monitoringPurposeOptionsType, monitoringPurpose),
      groundwaterAspect: isValidEnum(groundwaterAspectOptionsType, groundwaterAspect),
      startDateMonitoring: startDateMonitoring?.toISOString().slice(0, 10) ?? "",
    },
    metadata: {
      deliveryAccountableParty: deliveryAccountableParty ?? "",
      qualityRegime: isValidEnum(qualityRegimeOptionsType, qualityRegime),
      projectNumber: "",
      correctionReason: "eigenCorrectie",
      broId,
    },
  };
};

// UPLOAD TASK
export const convertGMNUploadTaskToCreateTask = ({
  projectNumber,
  metadata,
  sourcedocumentData,
}: UploadTask): CreateGMNTask => {
  const { qualityRegime, deliveryAccountableParty } = metadata as { [key: string]: string };
  const { name, deliveryContext, monitoringPurpose, groundwaterAspect, startDateMonitoring } =
    sourcedocumentData as { [key: string]: string };

  // Only include minimal, correct structure in sourcedocumentData
  return {
    metadata: {
      deliveryAccountableParty: deliveryAccountableParty,
      qualityRegime: isValidEnum(qualityRegimeOptionsType, qualityRegime),
      projectNumber,
    },
    sourcedocumentData: {
      measuringPoints: [], // Always empty for create
      objectIdAccountableParty: "",
      name: name,
      deliveryContext: isValidEnum(deliveryContextGMNOptionsType, deliveryContext),
      monitoringPurpose: isValidEnum(monitoringPurposeOptionsType, monitoringPurpose),
      groundwaterAspect: isValidEnum(groundwaterAspectOptionsType, groundwaterAspect),
      startDateMonitoring: startDateMonitoring,
    },
  };
};
